// External libraries
import get from 'lodash/get';
import noop from 'lodash/noop';
import some from 'lodash/some';

import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';

// Zocdoc libraries
import { blue70, blue100, slate70 } from '@zocdoc-mezzanine/foundations-colors';
import { Button } from '@zocdoc-frontend-common/components-button';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { UnstyledLink } from '@zocdoc-frontend-common/components-link';

// CSDK
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { Row } from '@zocdoc-frontend-common/styles-grid';
import { buildSsoLoginUrl } from '@zocdoc-frontend-common/utils-react/buildSsoLoginUrl';

import { SUBMIT_MODE } from '../../constants';
import { SOFT_LOGIN_STATE } from './constants';
import { RULE_STATUS } from '../../../../utils/CreateAccountValidation/constants';

// local imports
import Form from '../Form';

import {
    FacebookLoginButtonTransparent,
    GoogleLoginButtonTransparent,
    AppleLoginButtonTransparent,
} from '@zocdoc-frontend-common/components-sociallogin';
import scrollToElement from 'scroll-to-element';

import EmailFields from './Email';
import DobPicker from './Dob';
import GenderPicker from './Gender';
import NameFields from './Name';
import { PasswordFieldWithModal } from './Password';
import TermsAndPrivacy from './TermsAndPrivacy';

import { lazyImport, LazyComponent } from '../../../LazyImportComponent';

const getLoginModal = lazyImport(
    () =>
        import(
            /* webpackChunkName: "loginmodal", webpackPrefetch: 40 */ '@zocdoc-frontend-common/components-log-in-modal'
        ),
    'LoginModal'
);

class CreateAccountFormView extends React.Component {
    static propTypes = {
        //sso
        facebookLoginUrl: PropTypes.string,
        googleLoginUrl: PropTypes.string,
        appleLoginUrl: PropTypes.string,
        onCloseLoginModal: PropTypes.func,
        onClickFacebookLogin: PropTypes.func,
        onClickGoogleLogin: PropTypes.func,

        //soft login exp
        loggedInUser: PropTypes.object,

        accountCreatedFailed: PropTypes.bool,
        accountCreated: PropTypes.bool,
        form: PropTypes.object,
        formErrors: PropTypes.object,
        inlineEmailLoading: PropTypes.bool,
        onShowLoginModal: PropTypes.func.isRequired,
        onClosePasswordModal: PropTypes.func,
        onBlur: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        onFocus: PropTypes.func.isRequired,
        onView: PropTypes.func.isRequired,
        onModified: PropTypes.func.isRequired,
        onSignInSuccess: PropTypes.func,
        onSubmit: PropTypes.func.isRequired,
        passwordStatusChecks: PropTypes.object,
        recordRememberMeAssignment: PropTypes.func,
        rememberMe: PropTypes.bool,
        returnUrl: PropTypes.string,
        resetLoginModal: PropTypes.func.isRequired,
        showPasswordModal: PropTypes.bool,
        showInlinePasswordStatusChecks: PropTypes.bool,
        showLoginModal: PropTypes.bool,
        showRememberMe: PropTypes.bool,

        submitted: PropTypes.bool,
        useDefaultRedirectAfterSignInSuccess: PropTypes.bool,

        __fakeImportLoginModal: PropTypes.object,
    };

    static defaultProps = {
        facebookLoginUrl: '',
        googleLoginUrl: '',
        appleLoginUrl: '',
        formErrors: {},
        form: {},
        onSignInSuccess: noop,
        onChange: noop,
        onFocus: noop,
        onModified: noop,
        onBlur: noop,
        onView: noop,
        onSubmit: noop,
        passwordStatusChecks: {
            PasswordLength: RULE_STATUS.UNSET,
            PasswordNumbers: RULE_STATUS.UNSET,
            PasswordSymbols: RULE_STATUS.UNSET,
            PasswordLetters: RULE_STATUS.UNSET,
            PasswordGuess: RULE_STATUS.UNSET,
        },
        recordLoginModalExperimentAssignment: noop,
        recordRememberMeAssignment: noop,
        rememberMe: true,
        returnUrl: '',
        showRememberMe: false,
        submitted: false,
        useDefaultRedirectAfterSignInSuccess: false,
        loggedInUser: {},
    };

    _nameRef = React.createRef();
    _passwordRef = React.createRef();
    _emailRef = React.createRef();
    _dateRef = React.createRef();
    _sexRef = React.createRef();
    _termsAndPrivacyRef = React.createRef();

    componentDidMount() {
        const { onShowLoginModal } = this.props;

        if (this.isSoftLoggedIn()) {
            onShowLoginModal();
        }
    }

    render() {
        const emailFields = this.maybeRenderEmailValidation();

        const title = this.renderTitle();
        const passwordModal = this.maybeRenderPasswordModal();
        const loginModal = this.maybeRenderLoginModal();

        return (
            <Form
                data-test="create-account-view"
                dateOfBirthPicker={this.renderDateOfBirthPicker()}
                emailFields={emailFields}
                loginModal={loginModal}
                nameFields={this.renderNameFields()}
                onSubmit={e => this.handleSubmit(e, SUBMIT_MODE.MODAL)}
                passwordModal={passwordModal}
                sexPicker={this.renderSexPicker()}
                submitButton={this.renderSubmitButton()}
                termsAndPrivacy={this.renderTermsAndPrivacy()}
                title={title}
            />
        );
    }

    isSoftLoggedIn = () => {
        const { loggedInUser } = this.props;
        return get(loggedInUser, 'login_state') === SOFT_LOGIN_STATE;
    };

    canPopulateSoftLoggedInUserEmail = () => {
        // only users with a password(essentially non-sso users) can log in via their email
        // caveat -- sso users can gain a password if the go through the reset password flow.
        const { loggedInUser } = this.props;
        return !!(
            this.isSoftLoggedIn() &&
            get(loggedInUser, 'has_password', false) &&
            get(loggedInUser, 'email', null)
        );
    };

    getSoftLoggedInEmail = () => {
        const { loggedInUser } = this.props;

        if (this.canPopulateSoftLoggedInUserEmail()) {
            return get(loggedInUser, 'email', null);
        }

        return null;
    };

    maybeScrollToRef = () => {
        const { formErrors, submitted } = this.props;
        const ease = 'in-out-circ';
        const duration = 500;

        const scrollToRef = ref => {
            const node = findDOMNode(ref.current);
            scrollToElement(node, {
                duration,
                ease,
            });
        };

        if (!submitted) {
            return;
        }

        if (formErrors.firstName || formErrors.lastName) {
            scrollToRef(this._nameRef);
            return;
        }

        if (formErrors.email || formErrors.confirmEmail) {
            scrollToRef(this._emailRef);
            return;
        }

        if (
            formErrors.birthYear ||
            formErrors.birthMonth ||
            formErrors.birthDay
        ) {
            scrollToRef(this._dateRef);
        }

        if (formErrors.gender) {
            scrollToRef(this._sexRef);
        }

        if (formErrors.termsOfService) {
            scrollToRef(this._termsAndPrivacyRef);
        }
    };

    handleSubmit = async (event, mode) => {
        event.preventDefault();

        const { onSubmit } = this.props;
        await onSubmit(mode); // shows validates and shows password modal
        this.maybeScrollToRef();
    };

    renderTitle() {
        const {
            facebookLoginUrl: baseFacebookLoginUrl,
            googleLoginUrl: baseGoogleLoginUrl,
            onClickFacebookLogin,
            onClickGoogleLogin,
            rememberMe,
            returnUrl,
            shouldShowNewUsersSSO,
        } = this.props;
        const facebookLoginUrl = buildSsoLoginUrl(
            baseFacebookLoginUrl,
            returnUrl,
            rememberMe,
            true
        );
        const googleLoginUrl = buildSsoLoginUrl(
            baseGoogleLoginUrl,
            returnUrl,
            rememberMe,
            true
        );

        return (
            <Row>
                <LogInText>
                    {`Existing user?`}
                    <LogInLink
                        data-uem-id="log-in-link"
                        data-test="log-in-link"
                        onClick={this.handleClickLogInLink}
                    >
                        {`Log in`}
                    </LogInLink>
                </LogInText>

                <Header>{`Book your appointment`}</Header>

                {shouldShowNewUsersSSO && (
                    <>
                        <SsoLoginButton
                            beforeClick={onClickGoogleLogin}
                            data-test="create-account-google-login-button"
                            href={googleLoginUrl}
                        >
                            <GoogleLoginButtonTransparent />
                        </SsoLoginButton>
                        <SsoLoginButton
                            beforeClick={onClickFacebookLogin}
                            data-test="create-account-facebook-login-button"
                            href={facebookLoginUrl}
                        >
                            <StyledFacebookLoginButton />
                        </SsoLoginButton>
                        {this.maybeRenderAppleLoginButton()}
                        <Divider>
                            <HorizontalLine />
                            <DividerText>{`or`}</DividerText>
                            <HorizontalLine />
                        </Divider>
                    </>
                )}
            </Row>
        );
    }

    // we won't enable this until ios13 ships
    maybeRenderAppleLoginButton = () => {
        const {
            appleLoginUrl: baseAppleLoginUrl,
            rememberMe,
            returnUrl,
            showRememberMe,
            onClickAppleLogin,
        } = this.props;
        const appleLoginUrl = buildSsoLoginUrl(
            baseAppleLoginUrl,
            returnUrl,
            rememberMe,
            showRememberMe
        );
        // note that we look at baseAppleLoginUrl here instead
        // of appleLoginUrl because we want to know if the prop was
        // set. buildSsoLoginUrl will always return something for
        // appleLoginUrl
        return baseAppleLoginUrl ? (
            <SsoLoginButton
                beforeClick={onClickAppleLogin}
                data-test="create-account-apple-login-button"
                href={appleLoginUrl}
            >
                <StyledAppleLoginButton />
            </SsoLoginButton>
        ) : null;
    };

    maybeRenderEmailValidation = () => {
        const {
            onFocus,
            onBlur,
            onChange,
            formErrors,
            onShowLoginModal,
            inlineEmailLoading,
            submitted,
        } = this.props;

        return (
            <EmailFields
                data-test="create-account-email-fields"
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={onChange}
                emailError={formErrors.email}
                confirmEmailError={formErrors.confirmEmail}
                ref={this._emailRef}
                handleClickLogin={onShowLoginModal}
                inlineEmailLoading={inlineEmailLoading}
                inlineEmailTaken={formErrors.emailTaken}
                submitted={submitted}
            />
        );
    };

    maybeRenderPasswordModal = () => {
        const {
            onChange,
            onFocus,
            onClosePasswordModal,
            passwordStatusChecks,
            showPasswordModal,
            showRememberMe,
        } = this.props;

        if (showPasswordModal) {
            return (
                <PasswordFieldWithModal
                    data-test="create-account-password-modal"
                    onChange={onChange}
                    onFocus={onFocus}
                    onCloseModal={onClosePasswordModal}
                    showRememberMe={showRememberMe}
                    statusChecks={passwordStatusChecks}
                    onSubmit={e => this.handleSubmit(e, SUBMIT_MODE.FULL)}
                />
            );
        }
    };

    renderNameFields = () => {
        const { onFocus, onChange, onBlur, formErrors } = this.props;

        return (
            <NameFields
                data-test="create-account-name-fields"
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                error={formErrors.firstName || formErrors.lastName}
                ref={this._nameRef}
            />
        );
    };

    renderDateOfBirthPicker = () => {
        const { onChange, onFocus, onBlur, formErrors } = this.props;

        return (
            <DobPicker
                data-test="create-account-date-of-birth-fields"
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                ref={this._dateRef}
                error={
                    formErrors.birthDay ||
                    formErrors.birthYear ||
                    formErrors.birthMonth
                }
            />
        );
    };

    renderSexPicker = () => {
        const { onChange, formErrors } = this.props;
        return (
            <GenderPicker
                data-test="create-account-gender-field"
                onChange={onChange}
                error={formErrors.gender}
                ref={this._sexRef}
            />
        );
    };
    renderTermsAndPrivacy = () => {
        const { onChange, onView, formErrors } = this.props;

        return (
            <TermsAndPrivacy
                data-test="create-account-terms-of-service"
                onChange={onChange}
                onView={onView}
                error={formErrors.termsOfService}
                ref={this._termsAndPrivacyRef}
            />
        );
    };

    renderSubmitButton = () => {
        return (
            <ButtonWrapper>
                <Button
                    data-test="submit-button"
                    data-uem-id="submit-button"
                    fluid
                >
                    Save and continue
                </Button>
                {this.maybeRenderButtonErrorText()}
            </ButtonWrapper>
        );
    };

    maybeRenderLoginModal() {
        const {
            facebookLoginUrl,
            googleLoginUrl,
            appleLoginUrl,
            onCloseLoginModal,
            showLoginModal,
            onSignInSuccess,
            recordRememberMeAssignment,
            returnUrl,
            showRememberMe,
            useDefaultRedirectAfterSignInSuccess,
            __fakeImportLoginModal,
        } = this.props;

        if (showLoginModal) {
            const LoginModal = __fakeImportLoginModal || getLoginModal();
            return (
                <LazyComponent componentName="LoginModal">
                    <LoginModal
                        facebookLoginUrl={facebookLoginUrl}
                        googleLoginUrl={googleLoginUrl}
                        appleLoginUrl={appleLoginUrl}
                        onClickClose={onCloseLoginModal}
                        onSignInSuccess={onSignInSuccess}
                        recordRememberMeAssignment={recordRememberMeAssignment}
                        returnUrl={returnUrl}
                        showRememberMe={showRememberMe}
                        useDefaultRedirectAfterSignInSuccess={
                            useDefaultRedirectAfterSignInSuccess
                        }
                        defaultEmailValue={this.getSoftLoggedInEmail()}
                    />
                </LazyComponent>
            );
        }
    }

    maybeRenderButtonErrorText = () => {
        const { formErrors, accountCreatedFailed, submitted } = this.props;
        if (some(formErrors) && submitted) {
            return (
                <ErrorMessage data-test="button-error-message">
                    Please complete all required fields.
                </ErrorMessage>
            );
        }
        if (accountCreatedFailed) {
            return (
                <ErrorMessage data-test="button-error-message">
                    Account creation failed, try again later.
                </ErrorMessage>
            );
        }
    };

    handleClickLogInLink = () => {
        const { resetLoginModal, onShowLoginModal } = this.props;
        resetLoginModal();
        onShowLoginModal();
    };
}

const ButtonWrapper = styled(Row)`
    margin-bottom: 44px;
    ${breakpoint.small} {
        margin-bottom: 164px;
    }
`;

const LogInText = styled.div`
    font: 13px/1.5 ${FontFamily.SharpSansSemibold};
    margin-bottom: 14px;
`;

const SsoLoginButton = styled(UnstyledLink)`
    display: block;
`;

const LogInLink = styled.span`
    border-bottom: 1px dotted ${blue70};
    cursor: pointer;
    color: ${blue70};
    margin-left: 6px;
`;

const Header = styled.h1`
    color: ${blue100};
    font: 28px/1.15 ${FontFamily.SharpSansBold};
    margin: 8px 0 16px;

    ${breakpoint.small} {
        margin-bottom: 30px;
    }
`;

const StyledFacebookLoginButton = styled(FacebookLoginButtonTransparent)`
    margin-top: 8px;
`;

const StyledAppleLoginButton = styled(AppleLoginButtonTransparent)`
    margin-top: 8px;
`;

const Divider = styled.div`
    align-items: center;
    display: flex;
    margin: 26px 0;
`;

const DividerText = styled.div`
    font: 13px/1.15 ${FontFamily.SharpSansMedium};
    margin: 0 12px;
`;

const HorizontalLine = styled.div`
    border-bottom: 1px solid ${slate70};
    flex-grow: 1;
`;

export default CreateAccountFormView;
